/*
 * Autorska prava pridržava Mali mol d.o.o. Pula
 * Copyright (c) 2016. by Mali mol d.o.o. Pula. All rights reserved.
 *
 * Korištenje ili javno objavljivanje ovog programskog koda ili bilo kojeg
 * njegovog dijela, nije dozvoljeno bez prethodnog pismenog odobrenja tvrtke Mali mol d.o.o.
 *
 * Mali mol d.o.o., Špiljavac 2, 52100 Pula, Croatia
 * <URL:http://www.malimol.hr>, <mailto:info@malimol.hr>
 */

import { defineComponent, toRef, Ref, ref, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "primevue/usetoast";
import { useCalculationService } from "@/services/Calculations";
import { useUtility } from "./utility";
import { useAuthStore } from "@/stores/auth";
import { useAppStore } from "@/stores/app";
import type { ICalculationRequest } from "@/types/ICalculationRequest";

import type {
  ISketchCalculation,
  ISketchCalculationFlat,
} from "@/types/ISketchCalculation";

export default defineComponent({
  name: "CalculationTable",
  props: {
    calculations: {
      type: Array as () => Array<ISketchCalculation>,
      required: true,
    },
    totalRecords: {
      type: Number,
      required: true,
    },
    work_price: {
      type: Number,
      required: false,
      default: 0,
    },
    margin: {
      type: Number,
      required: false,
      default: 0,
    },
    fluctuations: {
      type: Number,
      required: false,
      default: 0,
    },
  },
  setup(props, { emit }) {
    const { t, locale } = useI18n({ useScope: "global" });
    const toast = useToast();
    const sketchCalculationsFlat: Ref<Array<ISketchCalculationFlat>> = ref([]);
    const calculations = toRef(props, "calculations");
    const totalRecords = toRef(props, "totalRecords");
    const service = useCalculationService();
    const work_price = toRef(props, "work_price");
    const margin = toRef(props, "margin");
    const fluctuations = toRef(props, "fluctuations");
    const loading = toRef(service, "loading");
    const utility = useUtility();
    const totalArea_netto = ref(0);
    const totalArea_brutto = ref(0);
    const totalMass_netto = ref(0);
    const totalMass_brutto = ref(0);
    const totalWork_time = ref(0);
    const totalWork_price = ref(0);
    const totalMaterial_price = ref(0);
    const totalPrice = ref(0);
    const expandedRows = ref([]);
    const authStore = useAuthStore();
    const appStore = useAppStore();

    const dt = ref();
    const dt_flat = ref();

    onMounted(() => {
      console.log("CalculationTable component mounted");
      refresh();
      console.log("Work price: ", work_price.value);
      console.log("Margin: ", margin.value);
      console.log("Fluctuations: ", fluctuations.value);
    });

    const refresh = () => {
      console.log("refresh");
      totalArea_netto.value = 0;
      totalArea_brutto.value = 0;
      totalMass_netto.value = 0;
      totalMass_brutto.value = 0;
      totalWork_time.value = 0;
      totalWork_price.value = 0;
      totalMaterial_price.value = 0;
      totalPrice.value = 0;
      if (calculations.value) {
        calculations.value.forEach((calculation: ISketchCalculation) => {
          totalArea_netto.value += calculation.area_netto;
          totalArea_brutto.value += calculation.area_brutto;
          totalMass_netto.value += calculation.mass_netto;
          totalMass_brutto.value += calculation.mass_brutto;
          totalWork_time.value += calculation.work_time;
          totalWork_price.value += calculation.work_price;
          totalMaterial_price.value += calculation.material_price;
          totalPrice.value += calculation.totalPrice;
          calculation.prices.forEach((row) => {
            sketchCalculationsFlat.value.push({
              id: calculation.id,
              creator: calculation.creator,
              name: calculation.name,
              contract: calculation.contract,
              assembly: calculation.assembly,
              article: row.article,
              position: row.position,
              element: row.element,
              diameter: row.diameter,
              LDR: row.LDR,
              L1: row.L1,
              Lm: row.Lm,
              L2: row.L2,
              quantity: row.quantity,
              units: row.units,
              area_netto: row.area_netto,
              waste: row.waste,
              area_brutto: row.area_brutto,
              material: row.material,
              thickness: row.thickness,
              mass_netto: row.mass_netto,
              mass_brutto: row.mass_brutto,
              work_time: row.work_time,
              work_price: row.work_price,
              material_price: row.material_price,
            });
          });
        });
      }
    };

    const expandAll = () => {
      let expanded = calculations.value.reduce(
        (acc, p) => (acc[p.id] = true) && acc,
        []
      );
      expandedRows.value = expanded;
    };

    const collapseAll = () => {
      expandedRows.value = [];
    };

    const exportCSV = () => {
      dt.value.exportCSV();
    };

    const exportExcel = () => {
      utility.exportExcel("calculation_report", "elements");
    };
    const exportExcelFlat = () => {
      utility.exportExcel("calculation_report_flat", "Calculation Report");
      // TableToExcel.convert(document.getElementById("calculation_report_flat"), {
      //   name: "Calculation Report.xlsx",
      //   sheet: {
      //     name: "Sheet 1",
      //   },
      // });
    };

    const onSave = () => {
      console.log("onSave");
      let request: ICalculationRequest = {
        date: new Date(),
        creator: authStore.userId,
        lang: locale.value,
        contract: appStore.calculationContract,
        content: JSON.stringify(calculations.value),
        area_netto: totalArea_netto.value,
        area_brutto: totalArea_brutto.value,
        mass_netto: totalMass_netto.value,
        mass_brutto: totalMass_brutto.value,
        work_time: totalWork_time.value,
        work_price: totalWork_price.value,
        material_price: totalMaterial_price.value,
        total_price: totalPrice.value,
        work_unit_price: work_price.value,
        margin: margin.value,
        fluctuations: fluctuations.value,
        subassemblies: appStore.subassemblies,
      };
      service
        .insert(request)
        .then(() => {
          if (appStore.calculationSaved) {
            toast.add({
              severity: "success",
              summary: t("message.data_saved"),
              life: 3000,
            });
          }
        })
        .catch((e) => {
          console.log(e);
          toast.add({
            severity: "error",
            summary: service.errorMessage.value,
            detail: e.message ? e.message : service.errorDetails.value,
            life: 3000,
          });
        });
    };

    watch(calculations, (currentValue: any, oldValue: any) => {
      console.log("Calculations watch calculations");
      refresh();
    });

    watch(work_price, (currentValue: any, oldValue: any) => {
      console.log("Calculations watch work_price", work_price.value);
    });

    watch(margin, (currentValue: any, oldValue: any) => {
      console.log("Calculations watch margin", margin.value);
    });

    watch(fluctuations, (currentValue: any, oldValue: any) => {
      console.log("Calculations watch fluctuations", fluctuations.value);
    });

    return {
      calculations,
      totalRecords,
      sketchCalculationsFlat,
      loading,
      appStore,
      totalArea_netto,
      totalArea_brutto,
      totalMass_netto,
      totalMass_brutto,
      totalWork_time,
      totalWork_price,
      totalMaterial_price,
      totalPrice,
      expandedRows,
      work_price,
      margin,
      fluctuations,
      dt,
      dt_flat,
      expandAll,
      collapseAll,
      exportCSV,
      exportExcel,
      exportExcelFlat,
      onSave,
      t,
    };
  },
});
